import type { UsePlayer } from '~/widgets/Player'
import type { IRewindState } from './useRewindState'

import { Events } from '@setplex/pureya'
import { useCallback, useEffect, useRef, useState } from 'react'

const GO_LIVE_ERROR_CODES = [
  'NE001', // Network Error
  'PE003', // Custom Error
  'PE004', // Aborted Error
  'PE005', // Decode Error
  'PE006', // Source Not Supported
  'PE010', // Timeout Error
  'SE001', // Generic Server Error
  'SE404', // Not Found
  'SE500', // Internal Server Error
]

interface UseGoLiveOnErrorParams {
  enabled: boolean
  player: UsePlayer
  rewindState: IRewindState
  contentId?: string | number | null
  contentKey: number | undefined
  enableSecondStage?: boolean
}

interface GoLiveOnErrorResult {
  showGoLivePopup: boolean
  handleGoLive: () => void
  isSecondStage: boolean
}

export const useGoLiveOnError = ({
  enabled,
  player,
  rewindState,
  contentId,
  contentKey,
  enableSecondStage = false,
}: UseGoLiveOnErrorParams): GoLiveOnErrorResult => {
  const [showGoLivePopup, setShowGoLivePopup] = useState(false)
  const goLiveClickedRef = useRef(false)
  const secondStageRef = useRef(false)
  const previousIsRewoundRef = useRef(false)
  const { isRewound } = rewindState

  const handleError = useCallback(() => {
    if (!enabled) return

    const error = player.instance?.error()
    if (!error) return

    const errorCode =
      typeof error.code === 'symbol'
        ? (error.code as symbol).description
        : String(error.code)

    const isTargetError = GO_LIVE_ERROR_CODES.includes(errorCode || '')

    if (isTargetError && isRewound && !goLiveClickedRef.current) {
      setShowGoLivePopup(true)
      secondStageRef.current = false
    } else if (
      enableSecondStage &&
      isTargetError &&
      !isRewound &&
      goLiveClickedRef.current
    ) {
      setShowGoLivePopup(true)
      secondStageRef.current = true
    }
  }, [enabled, player.instance, isRewound, enableSecondStage])

  const handleGoLive = useCallback(() => {
    if (player.instance?.error()) {
      player.instance.error(null as any)
    }

    player.instance?.trigger(Events.Player.GoLiveOnError)

    goLiveClickedRef.current = true
  }, [player.instance])

  useEffect(() => {
    setShowGoLivePopup(false)
  }, [contentKey])

  useEffect(() => {
    if (!enabled || !player.events.on) return

    const handleLoadStart = () => {
      if (showGoLivePopup) {
        setShowGoLivePopup(false)
      }
    }

    player.events.on('error:goLiveOnError', handleError)
    player.events.on('loadstart:goLiveOnError', handleLoadStart)

    return () => {
      player.events.off?.('error:goLiveOnError')
      player.events.off?.('loadstart:goLiveOnError')
    }
  }, [enabled, player.events, handleError, showGoLivePopup])

  useEffect(() => {
    // If successfully went live after first stage, just hide popup
    if (!isRewound && goLiveClickedRef.current && !secondStageRef.current) {
      setShowGoLivePopup(false)
    }

    // If new rewind cycle starts (transition from live to rewind), reset flags
    if (isRewound && !previousIsRewoundRef.current) {
      goLiveClickedRef.current = false
      secondStageRef.current = false
    }

    // Update previous value
    previousIsRewoundRef.current = isRewound
  }, [isRewound])

  useEffect(() => {
    setShowGoLivePopup(false)
    goLiveClickedRef.current = false
    secondStageRef.current = false
    previousIsRewoundRef.current = false
  }, [contentId])

  return {
    showGoLivePopup,
    handleGoLive,
    isSecondStage: secondStageRef.current,
  }
}
